import { formatCurrency } from './currency';

/**
 * Budget-card ring: spent / budget.
 *
 * Mobile source of truth: `mobile/components/cards/BudgetCard.tsx`
 * (`spentPercentage = totalSpent / totalBudget`, over-budget when > 100).
 * Mobile sums `insights.categorySpent`; if that map is missing, spent is 0.
 */

export function getBudgetTotalSpent(budgetInsights) {
  const categorySpent = budgetInsights?.categorySpent;
  if (!categorySpent || typeof categorySpent !== 'object') return 0;
  return Object.values(categorySpent).reduce(
    (sum, amount) => sum + Number(amount || 0),
    0
  );
}

/** Mobile: over-spent is `themeColors.primary`; otherwise `themeColors.status.ready`. */
export function getBudgetSpentRingColor(isOverBudget, tokens) {
  return isOverBudget ? tokens.primary : tokens.status.ready;
}

export function getBudgetSpentRing({ totalBudget, totalSpent }) {
  const budget = Number(totalBudget || 0);
  const spent = Number(totalSpent || 0);
  const spentPercentage = budget > 0 ? (spent / budget) * 100 : 0;
  const clampedPercentage = Math.min(100, Math.max(0, spentPercentage));
  const isOverBudget = spentPercentage > 100;
  const remainingToSpend = budget - spent;
  const labelPercentage = Math.max(0, spentPercentage);

  if (isOverBudget) {
    const over = Math.abs(remainingToSpend);
    return {
      spentPercentage,
      clampedPercentage,
      labelPercentage,
      isOverBudget,
      remainingToSpend,
      message: 'Over',
      subMessage: formatCurrency(over),
      tooltip: `${spentPercentage.toFixed(1)}% spent, ${formatCurrency(over)} over budget`,
    };
  }

  if (budget > 0 && remainingToSpend <= 0) {
    return {
      spentPercentage,
      clampedPercentage,
      labelPercentage,
      isOverBudget,
      remainingToSpend,
      message: 'Spent',
      subMessage: '100%',
      tooltip: 'All of this budget has been spent',
    };
  }

  return {
    spentPercentage,
    clampedPercentage,
    labelPercentage,
    isOverBudget,
    remainingToSpend,
    message: 'Left',
    subMessage: formatCurrency(Math.max(0, remainingToSpend)),
    tooltip: `${spentPercentage.toFixed(1)}% spent, ${formatCurrency(Math.max(0, remainingToSpend))} left`,
  };
}
